import * as THREE from "three";

/**
 * PRAYOGASHAALA PHYSICS PARK — Phase 1 Roller-Coaster Track
 *
 * Design: 8 distinct ramp sections, ~700 world-unit span, 4-5 minute run.
 * Each section has a different visual identity — the environment scenery
 * should match the zone labels:
 *
 *   ZONE 1 — LAUNCH VALLEY   (flat launch, gentle roll, pine forest)
 *   ZONE 2 — SUMMIT CLIMB 1  (big first hill 40 m — slow crawl up, FAST plunge)
 *   ZONE 3 — CANYON SWEEP    (red rock canyon, sharp horizontal curve, H1)
 *   ZONE 4 — HIGHLAND RIDGE  (medium hill 28 m, mist, H2 at ridge crest)
 *   ZONE 5 — LAKE VALLEY     (water-level flat, wide S-bends, H3)
 *   ZONE 6 — COASTAL CLIFF   (cliff-edge run, dramatic drop, H4, nozzle gate)
 *   ZONE 7 — JUNGLE SPIRAL   (low jungle, tight undulations, impulse gate)
 *   ZONE 8 — DISTANCE PLAIN  (long straight, distance-time markers)
 *   ZONE 9 — SUMMIT CLIMB 2  (final tall hill 35 m, collision gate, H5)
 *   ZONE 10 — FINISH VALLEY  (sweeping descent, H6, arrive at station)
 *
 * Gravity physics: aAlong = engine + g·sin(slope) − friction − airDrag
 * Climbs are SLOW, descents are FAST — feels like a real roller-coaster.
 *
 * Scenario gates fire at ≥30s elapsed AND ≥30s since last gate.
 * The 3 gates are spread across s ≈ 0.38, 0.60, 0.80 (well apart).
 */

// ── Control points: [x, y, z] — y is height above ground ──
// The track winds through a 700 × 600 world-unit arena.
export const trackPoints = [

  // ─── ZONE 1: LAUNCH VALLEY (flat, pine forest both sides) ───────────────────
  new THREE.Vector3(   0,  1,    0),
  new THREE.Vector3(  50,  1,    0),
  new THREE.Vector3( 100,  1,    0),
  new THREE.Vector3( 150,  2,   -5),
  new THREE.Vector3( 200,  2,  -15),
  new THREE.Vector3( 240,  3,  -30),

  // ─── ZONE 2: SUMMIT CLIMB 1 ── big first hill, peak 40 m ────────────────────
  new THREE.Vector3( 265,  8,  -45),
  new THREE.Vector3( 280, 18,  -60),
  new THREE.Vector3( 290, 30,  -75),
  new THREE.Vector3( 295, 40,  -88),  // CREST 1 — 40 m

  // fast plunge — east side
  new THREE.Vector3( 290, 27,  -105),
  new THREE.Vector3( 275, 12,  -125),
  new THREE.Vector3( 255,  3,  -145),
  new THREE.Vector3( 230,  1,  -165),

  // ─── ZONE 3: CANYON SWEEP ── red canyon, turns right (west), Hazard H1 ──────
  new THREE.Vector3( 195,  2,  -185),
  new THREE.Vector3( 155,  3,  -200),  // curve begins — H1
  new THREE.Vector3( 110,  2,  -205),
  new THREE.Vector3(  70,  2,  -195),
  new THREE.Vector3(  35,  3,  -180),
  new THREE.Vector3(   5,  3,  -160),

  // ─── ZONE 4: HIGHLAND RIDGE ── medium hill 28 m, mist zone ─────────────────
  new THREE.Vector3( -25,  5,  -140),
  new THREE.Vector3( -50, 12,  -118),
  new THREE.Vector3( -65, 22,  -100),  // ascending ridge
  new THREE.Vector3( -70, 28,  -85),   // CREST 2 — 28 m, H2 zone
  new THREE.Vector3( -65, 18,  -68),
  new THREE.Vector3( -50,  8,  -50),
  new THREE.Vector3( -30,  2,  -33),

  // ─── ZONE 5: LAKE VALLEY ── water-level flat, wide S-bends, H3 ─────────────
  new THREE.Vector3(  -5,  1,  -18),
  new THREE.Vector3(  20,  1,    5),
  new THREE.Vector3(  40,  1,   25),   // S-bend 1
  new THREE.Vector3(  30,  1,   50),
  new THREE.Vector3(  10,  1,   70),   // H3 region
  new THREE.Vector3( -20,  1,   85),
  new THREE.Vector3( -50,  2,   95),

  // ─── ZONE 6: COASTAL CLIFF ── turns south-west, cliff drop, H4 ─────────────
  new THREE.Vector3( -85,  5,  100),
  new THREE.Vector3(-120,  8,  108),   // begins climbing onto cliff
  new THREE.Vector3(-155, 15,  112),
  new THREE.Vector3(-180, 20,  108),   // cliff edge crest — H4 curve
  new THREE.Vector3(-205, 12,  100),
  new THREE.Vector3(-225,  4,   88),
  new THREE.Vector3(-238,  1,   72),   // bottom — nozzle gate near here

  // ─── ZONE 7: JUNGLE SPIRAL ── low hills, tight undulations ─────────────────
  new THREE.Vector3(-250,  3,   52),
  new THREE.Vector3(-260,  8,   30),   // mini rise
  new THREE.Vector3(-265, 13,   10),   // 13 m bump
  new THREE.Vector3(-260,  7,   -8),
  new THREE.Vector3(-248,  2,  -25),
  new THREE.Vector3(-230,  4,  -42),   // H5 — tight jungle turn
  new THREE.Vector3(-210,  6,  -58),   // impulse gate zone
  new THREE.Vector3(-190,  2,  -72),
  new THREE.Vector3(-165,  1,  -82),

  // ─── ZONE 8: DISTANCE PLAIN ── long flat straight, distance-time markers ────
  new THREE.Vector3(-135,  1,  -88),
  new THREE.Vector3(-100,  1,  -90),
  new THREE.Vector3( -60,  1,  -90),
  new THREE.Vector3( -20,  1,  -90),
  new THREE.Vector3(  20,  1,  -90),
  new THREE.Vector3(  60,  1,  -90),   // distance markers every 10 m here
  new THREE.Vector3( 100,  1,  -90),
  new THREE.Vector3( 140,  1,  -88),

  // ─── ZONE 9: SUMMIT CLIMB 2 ── final tall hill 35 m, collision gate ─────────
  new THREE.Vector3( 170,  2,  -80),
  new THREE.Vector3( 195,  8,  -65),
  new THREE.Vector3( 215, 18,  -50),
  new THREE.Vector3( 228, 28,  -37),
  new THREE.Vector3( 232, 35,  -26),   // CREST 3 — 35 m (collision gate just before)
  new THREE.Vector3( 228, 23,  -14),
  new THREE.Vector3( 215, 10,   -3),
  new THREE.Vector3( 195,  2,    5),

  // ─── ZONE 10: FINISH VALLEY ── sweeping descent, H6, station ────────────────
  new THREE.Vector3( 170,  1,   12),   // H6 curve — final brake
  new THREE.Vector3( 140,  1,   10),
  new THREE.Vector3( 110,  1,    6),
  new THREE.Vector3(  80,  1,    3),
  new THREE.Vector3(  50,  1,    1),
  new THREE.Vector3(  20,  1,    0),
  new THREE.Vector3(   0,  1,    0),   // FINISH — station (back near start)
];

// Closed=false — a journey, not a lap
export const trackCurve = new THREE.CatmullRomCurve3(trackPoints, false, "centripetal", 0.5);

// Total track length in metres (Three.js world units ≈ metres here)
export const trackLength = trackCurve.getLength();

// ── Zones ──
export const zones = [
  { id: "ZONE_1",  name: "Launch Valley",    startS: 0.00, endS: 0.10 },
  { id: "ZONE_2",  name: "Summit Climb 1",   startS: 0.10, endS: 0.23 },
  { id: "ZONE_3",  name: "Canyon Sweep",     startS: 0.23, endS: 0.34 },
  { id: "ZONE_4",  name: "Highland Ridge",   startS: 0.34, endS: 0.44 },
  { id: "ZONE_5",  name: "Lake Valley",      startS: 0.44, endS: 0.54 },
  { id: "ZONE_6",  name: "Coastal Cliff",    startS: 0.54, endS: 0.63 },
  { id: "ZONE_7",  name: "Jungle Spiral",    startS: 0.63, endS: 0.72 },
  { id: "ZONE_8",  name: "Distance Plain",   startS: 0.72, endS: 0.82 },
  { id: "ZONE_9",  name: "Summit Climb 2",   startS: 0.82, endS: 0.92 },
  { id: "ZONE_10", name: "Finish Valley",    startS: 0.92, endS: 1.00 },
];

// ── Stations ──
export const stations = [
  { s: 0.02, label: "STATION 1 — Launch Pad",      zone: "ZONE_1"  },
  { s: 0.27, label: "STATION 2 — Canyon Floor",    zone: "ZONE_3"  },
  { s: 0.50, label: "STATION 3 — Lake Valley",     zone: "ZONE_5"  },
  { s: 0.68, label: "STATION 4 — Jungle Base",     zone: "ZONE_7"  },
  { s: 0.76, label: "STATION 5 — Distance Plain",  zone: "ZONE_8"  },
];

// ── Helper: get full frame at parameter t ──
export function getTrackFrame(curve, t) {
  t = ((t % 1.0) + 1.0) % 1.0;
  if (t >= 1) t = 0.999;

  const point   = curve.getPointAt(t);
  const tangent = curve.getTangentAt(t).normalize();

  const horizontalLen = Math.sqrt(tangent.x ** 2 + tangent.z ** 2);
  const slope = Math.atan2(tangent.y, horizontalLen);

  const dt = 0.003;
  const t2 = Math.min(0.999, t + dt);
  const t3 = Math.min(0.999, t + 2 * dt);
  const p2 = curve.getPointAt(t2);
  const p3 = curve.getPointAt(t3);
  const v1 = new THREE.Vector3().subVectors(p2, point);
  const v2 = new THREE.Vector3().subVectors(p3, p2);
  const a = v1.length(), b = v2.length();
  const c = new THREE.Vector3().subVectors(p3, point).length();
  const sp = (a + b + c) / 2;
  const areaSq = Math.max(0, sp * (sp - a) * (sp - b) * (sp - c));
  let radius = 10000;
  if (areaSq > 1e-10) {
    const area = Math.sqrt(areaSq);
    radius = (a * b * c) / (4 * area);
  }

  const defaultUp = new THREE.Vector3(0, 1, 0);
  let right = new THREE.Vector3().crossVectors(tangent, defaultUp);
  if (right.lengthSq() < 0.001) right = new THREE.Vector3(1, 0, 0);
  else right.normalize();
  const up = new THREE.Vector3().crossVectors(right, tangent).normalize();

  return { point, tangent, up, right, radius, slope };
}

export function lookaheadCurve(curve, s, distS) {
  const futureS = Math.min(0.999, s + distS);
  return getTrackFrame(curve, futureS);
}

// ── Hazard curves — spread across zones, real v_max = √(g·r·cosθ) ──
// Placed at the tightest bend in each zone; distances are generous given the
// longer track so they fire ≈ 35–45 s apart at cruising speed.
export const hazards = [
  {
    id:          "H1",
    label:       "Canyon Hairpin — watch your speed!",
    checkpointS: 0.265,
    sStart:      0.270,
    sEnd:        0.310,
    rTarget:     45,
    slopeTarget: 0.0,
  },
  {
    id:          "H2",
    label:       "Highland Ridge Crest — gravity pulls you over",
    checkpointS: 0.375,
    sStart:      0.380,
    sEnd:        0.415,
    rTarget:     55,
    slopeTarget: 0.06,
  },
  {
    id:          "H3",
    label:       "Lake S-Bend — centripetal grip test",
    checkpointS: 0.478,
    sStart:      0.483,
    sEnd:        0.510,
    rTarget:     40,
    slopeTarget: 0.0,
  },
  {
    id:          "H4",
    label:       "Coastal Cliff Edge — don't go over!",
    checkpointS: 0.570,
    sStart:      0.575,
    sEnd:        0.600,
    rTarget:     35,
    slopeTarget: 0.04,
  },
  {
    id:          "H5",
    label:       "Jungle Hairpin — tight jungle turn",
    checkpointS: 0.655,
    sStart:      0.660,
    sEnd:        0.685,
    rTarget:     38,
    slopeTarget: 0.0,
  },
  {
    id:          "H6",
    label:       "Finish Curve — brake into station",
    checkpointS: 0.933,
    sStart:      0.938,
    sEnd:        0.960,
    rTarget:     60,
    slopeTarget: 0.0,
  },
];

/**
 * Scenario gates — 3 POE events directly tied to the incline ride experience.
 *
 * All three questions use physics the player is LITERALLY experiencing:
 *  SC1 — Gravity on the slope (component along incline, why the cart slows climbing)
 *  SC2 — Banked curve (why banking lets you corner faster than flat friction alone)
 *  SC3 — Air resistance at speed (why the cart accelerates less on fast descents)
 *
 * PLACEMENT: s = 0.38, 0.60, 0.83 (position filter only).
 * TIMING: GameLoop enforces ≥ 30s warmup then ≥ 30s between each gate.
 */
export const scenarioGates = [
  {
    // Fires after Canyon Sweep / Highland Ridge — student just crawled up a big slope
    id:          "SC1",
    kind:        "incline",
    label:       "🏔 GRAVITY ON THE SLOPE — Why does the cart slow down climbing?",
    checkpointS: 0.38,
    scenario: {
      setup: "You just climbed a ramp at 30° to the horizontal. The cart has mass 800 kg and g = 10 m/s². What is the component of gravity pulling the cart BACK down the slope?",
      reason: {
        question: "On an inclined plane at angle θ, gravity (mg) acts vertically downward. Which component opposes motion up the slope?",
        options: [
          { id: "A", text: "mg·sin θ — the component parallel to the slope surface", correct: true },
          { id: "B", text: "mg·cos θ — the component into the slope surface", correct: false, whyWrong: "mg·cosθ is the force pressing the cart into the track (normal force), not the one slowing it up the slope." },
          { id: "C", text: "mg itself — the full weight pulls back", correct: false, whyWrong: "The full weight is vertical; only its component along the slope (mg·sinθ) opposes motion up the incline." },
          { id: "D", text: "mg·tan θ — the tangent component", correct: false, whyWrong: "Tangent is not a force component used here. Resolve along and perpendicular to the slope using sin and cos." },
        ],
      },
      formula: {
        question: "The deceleration (retardation) caused by gravity on a slope at angle θ is:",
        options: [
          { id: "A", text: "a = g·sin θ", correct: true },
          { id: "B", text: "a = g·cos θ", correct: false, whyWrong: "g·cosθ gives the acceleration into the slope surface — that's what creates the normal force, not the slowdown." },
          { id: "C", text: "a = g / sin θ", correct: false, whyWrong: "Dividing by sinθ makes the value larger than g, which is physically impossible for a component of gravity." },
          { id: "D", text: "a = g·sin θ·cos θ", correct: false, whyWrong: "That extra cosθ has no basis here — the along-slope deceleration is simply g·sinθ." },
        ],
      },
      value: {
        question: "The ramp is at 30°. Cart mass = 800 kg, g = 10 m/s². Calculate the gravitational force pulling the cart back down the slope in N.",
        hint: "Force along slope = m × g × sin(θ). What is sin(30°)?",
        correct: 4000,
        tolerance: 100,
        unit: "N",
        whyWrongHigh: "Too high — check sin(30°). It is exactly 0.5, not 1. So the force is m × g × 0.5.",
        whyWrongLow:  "Too low — make sure you multiplied by mass (800 kg), not just g·sinθ alone.",
      },
    },
  },
  {
    // Fires in Coastal Cliff / Jungle Spiral — student just experienced a tight banked turn
    id:          "SC2",
    kind:        "banking",
    label:       "🔄 BANKED CURVE — Why can you corner faster on a banked track?",
    checkpointS: 0.60,
    scenario: {
      setup: "The track curves with radius r = 50 m and is banked at angle θ = 20°. Cart mass = 800 kg, g = 10 m/s². What is the ideal cornering speed — no friction needed?",
      reason: {
        question: "On a banked curve with no friction, what force provides the centripetal acceleration?",
        options: [
          { id: "A", text: "The horizontal component of the normal force: N·sin θ", correct: true },
          { id: "B", text: "Friction from the track surface pushing inward", correct: false, whyWrong: "At the ideal banking speed, no friction is needed — the normal force's horizontal component does the whole job." },
          { id: "C", text: "The vertical component of the normal force: N·cos θ", correct: false, whyWrong: "N·cosθ balances gravity (vertically), it does not point toward the centre of the curve." },
          { id: "D", text: "Gravity alone pulls the cart inward", correct: false, whyWrong: "Gravity acts downward, not sideways. The banking tilts the normal force to create the inward component." },
        ],
      },
      formula: {
        question: "At the ideal banking speed (no friction), the formula is:",
        options: [
          { id: "A", text: "v = √(r·g·tan θ)", correct: true },
          { id: "B", text: "v = √(r·g·sin θ)", correct: false, whyWrong: "Using sinθ only counts one component. Dividing N·sinθ = mv²/r by N·cosθ = mg gives v² = rg·tanθ." },
          { id: "C", text: "v = √(μ·r·g)", correct: false, whyWrong: "That is the flat-track friction formula. On a banked curve without friction, the angle θ provides the turning force, not μ." },
          { id: "D", text: "v = √(r·g / tan θ)", correct: false, whyWrong: "Inverted — dividing by tanθ gives a smaller speed, but a steeper bank allows higher speed, not lower." },
        ],
      },
      value: {
        question: "r = 50 m, θ = 20°, g = 10 m/s². Calculate the ideal banking speed in m/s. (tan 20° ≈ 0.364)",
        hint: "Use v = √(r × g × tan θ). Substitute r = 50, g = 10, tan(20°) ≈ 0.364.",
        correct: 13.5,
        tolerance: 0.4,
        unit: "m/s",
        whyWrongHigh: "Too high — check tan(20°). It is approximately 0.364, not tan(45°) = 1.",
        whyWrongLow:  "Too low — remember to take the square root at the end: v = √(50 × 10 × 0.364).",
      },
    },
  },
  {
    // Fires on Summit Climb 2 — student just experienced a fast descent where speed stopped increasing
    id:          "SC3",
    kind:        "drag",
    label:       "💨 AIR RESISTANCE — Why did the descent speed cap out?",
    checkpointS: 0.83,
    scenario: {
      setup: "On the fast descent, the cart (mass 800 kg, frontal area 2 m², drag coefficient Cᴅ = 0.8) reached a constant top speed — terminal velocity — where air drag exactly cancelled the gravitational drive. Air density ρ = 1.2 kg/m³. What was that terminal speed?",
      reason: {
        question: "At terminal velocity on a slope, the net force along the slope is zero. This means:",
        options: [
          { id: "A", text: "Gravitational component down the slope = air drag force (opposing motion)", correct: true },
          { id: "B", text: "Gravity completely disappears at high speed", correct: false, whyWrong: "Gravity never disappears. Terminal velocity is when drag grows large enough to exactly balance the gravitational drive." },
          { id: "C", text: "The drag force equals the full weight mg", correct: false, whyWrong: "Drag balances only the along-slope component of gravity (mg·sinθ), not the full weight." },
          { id: "D", text: "Speed stops increasing because friction increases with speed", correct: false, whyWrong: "Solid-surface kinetic friction does not increase with speed. Air drag (∝ v²) is what grows until it equals the driving gravity component." },
        ],
      },
      formula: {
        question: "Setting gravitational drive equal to air drag on a slope at angle θ, terminal speed v_t is:",
        options: [
          { id: "A", text: "v_t = √( 2mg·sinθ / (ρ·Cᴅ·A) )", correct: true },
          { id: "B", text: "v_t = 2mg·sinθ / (ρ·Cᴅ·A)", correct: false, whyWrong: "No square root — drag force is ½ρCᴅAv², so solving for v requires taking the square root." },
          { id: "C", text: "v_t = √( mg / (ρ·Cᴅ·A) )", correct: false, whyWrong: "This uses the full weight mg rather than only the slope component mg·sinθ." },
          { id: "D", text: "v_t = √( mg·cosθ / (ρ·Cᴅ·A) )", correct: false, whyWrong: "cosθ gives the normal force component, not the along-slope driving force. You need sinθ." },
        ],
      },
      value: {
        question: "Slope angle θ = 15°. m = 800 kg, g = 10, ρ = 1.2 kg/m³, Cᴅ = 0.8, A = 2 m². Find terminal speed in m/s. (sin 15° ≈ 0.259)",
        hint: "Compute 2mg·sinθ first, then divide by (ρ·Cᴅ·A), then take the square root.",
        correct: 46.4,
        tolerance: 1.5,
        unit: "m/s",
        whyWrongHigh: "Too high — make sure you used sinθ (≈ 0.259), not sinθ = 1, and included ρ·Cᴅ·A in the denominator.",
        whyWrongLow:  "Too low — check that you included the factor of 2 in the numerator (from ½ρCᴅAv² → v² = 2mg·sinθ/ρCᴅA).",
      },
    },
  },
];

/**
 * Compute the minimum radius (and slope) across a hazard window.
 * Binding constraint: v_max = √(g · r_min · cosθ)
 */
export function hazardMinRadius(hz) {
  let rMin = 100000;
  let slopeAtMin = 0;
  const steps = 40;
  for (let i = 0; i <= steps; i++) {
    const t = hz.sStart + (i / steps) * (hz.sEnd - hz.sStart);
    const { radius, slope } = getTrackFrame(trackCurve, Math.min(0.999, t));
    if (radius < rMin) { rMin = radius; slopeAtMin = slope; }
  }
  rMin = Math.max(8, Math.min(rMin, 9000));
  return { rMin, slope: slopeAtMin };
}
